import { getAgentApiHost } from "@/lib/agent-api-host";

import {
  buildBrowseMenuIndex,
  type BrowseMenuIndex,
  type BrowseMenuItem,
} from "./browse-menu-tree";

const BROWSE_MENU_CACHE_KEY = "browse-menu-cache";
const BROWSE_MENU_ETAG_KEY = "browse-menu-etag";

export interface BrowseMenuSnapshot {
  status: "idle" | "loading" | "ready" | "error";
  index: BrowseMenuIndex | null;
  error: string | null;
}

const IDLE: BrowseMenuSnapshot = { status: "idle", index: null, error: null };

type CachedMenu = { representation: "full"; items: BrowseMenuItem[]; etag: string | null };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonemptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

// Validate shape and safe traversal before indexing. Preserve the existing
// index builder's tolerance for isolated dangling edges in published artifacts.
function validateItems(value: unknown): BrowseMenuItem[] {
  if (!Array.isArray(value)) throw new Error("Invalid browse menu response");
  const items = new Map<string, BrowseMenuItem>();
  for (const item of value) {
    if (
      !isRecord(item) ||
      !isNonemptyString(item.uuid) ||
      typeof item.display_name !== "string" ||
      !isRecord(item.search_query) ||
      (item.children != null &&
        (!Array.isArray(item.children) || !item.children.every(isNonemptyString))) ||
      (item.search_query.facets != null &&
        (!Array.isArray(item.search_query.facets) ||
          !item.search_query.facets.every(
            (facet) =>
              isRecord(facet) &&
              isNonemptyString(facet.name) &&
              Array.isArray(facet.values) &&
              facet.values.every((entry: unknown) => typeof entry === "string")
          ))) ||
      items.has(item.uuid)
    ) {
      throw new Error("Invalid browse menu response");
    }
    items.set(item.uuid, item as unknown as BrowseMenuItem);
  }
  const childIds = [...items.values()].flatMap((item) => item.children ?? []);
  if (childIds.length > 0 && !childIds.some((id) => items.has(id))) {
    // Root-only API responses retain child IDs but omit all descendants. Do not
    // let that representation poison the full-menu cache. A partially complete
    // full graph can still render its resolvable branches as it did before.
    throw new Error("Incomplete browse menu response");
  }
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (id: string) => {
    if (visiting.has(id)) throw new Error("Cyclic browse menu response");
    if (visited.has(id)) return;
    const item = items.get(id);
    if (!item) return;
    visiting.add(id);
    (item.children ?? []).forEach(visit);
    visiting.delete(id);
    visited.add(id);
  };
  items.forEach((_, id) => visit(id));
  return value as BrowseMenuItem[];
}

function readCache(): { items: BrowseMenuItem[] | null; etag: string | null } {
  let etag: string | null = null;
  try {
    // Accept the existing array/separate-ETag cache on the first actual load.
    etag = window.localStorage.getItem(BROWSE_MENU_ETAG_KEY);
    const raw = window.localStorage.getItem(BROWSE_MENU_CACHE_KEY);
    if (!raw) return { items: null, etag };
    const parsed: unknown = JSON.parse(raw);
    if (isRecord(parsed)) {
      if (parsed.representation !== "full") return { items: null, etag: null };
      etag = typeof parsed.etag === "string" ? parsed.etag : null;
      return { items: validateItems(parsed.items), etag };
    }
    return { items: validateItems(parsed), etag };
  } catch {
    return { items: null, etag };
  }
}

function writeCache(items: BrowseMenuItem[], etag: string | null): void {
  try {
    const cache: CachedMenu = { representation: "full", items, etag };
    // A single storage write commits the body and its ETag atomically, including
    // across tabs. Legacy readers treat this envelope as a cache miss.
    window.localStorage.setItem(BROWSE_MENU_CACHE_KEY, JSON.stringify(cache));
    window.localStorage.removeItem(BROWSE_MENU_ETAG_KEY);
  } catch {
    // Storage denial/quota failures never prevent an in-memory success.
  }
}

async function loadItems(): Promise<BrowseMenuItem[]> {
  const cached = readCache();
  const url = new URL("/shop-agent/browse-menu", getAgentApiHost());
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15_000);
  const request = (etag: string | null) =>
    fetch(url.toString(), {
      method: "GET",
      credentials: "omit",
      signal: controller.signal,
      headers: etag ? { "If-None-Match": etag } : {},
    });
  try {
    let response = await request(cached.etag);
    if (response.status === 304) {
      const responseEtag = response.headers.get("ETag");
      if (cached.items && cached.etag && (!responseEtag || responseEtag === cached.etag)) {
        return cached.items;
      }
      response = await request(null);
    }
    if (response.status !== 200) {
      throw new Error(`Browse menu fetch failed: ${response.status} ${response.statusText}`);
    }
    const items = validateItems(await response.json());
    writeCache(items, response.headers.get("ETag"));
    return items;
  } catch (error) {
    if (controller.signal.aborted) throw new Error("Browse menu request timed out after 15000ms");
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

function createResource() {
  let snapshot = IDLE;
  let inFlight: Promise<BrowseMenuIndex> | null = null;
  const listeners = new Set<() => void>();
  const publish = (next: BrowseMenuSnapshot) => {
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    ensureLoaded(): Promise<BrowseMenuIndex> {
      if (inFlight) return inFlight;
      if (snapshot.index) return Promise.resolve(snapshot.index);
      // Defer loading until the shared promise is installed, including for
      // subscribers that call ensureLoaded synchronously on a state change.
      inFlight = Promise.resolve()
        .then(loadItems)
        .then((items) => {
          const index = buildBrowseMenuIndex(items);
          publish({ status: "ready", index, error: null });
          return index;
        })
        .catch((error: unknown) => {
          publish({
            status: "error",
            index: null,
            error: error instanceof Error ? error.message : "Failed to load browse menu",
          });
          throw error;
        })
        .finally(() => {
          inFlight = null;
        });
      publish({ status: "loading", index: null, error: null });
      return inFlight;
    },
  };
}

// Created only in a browser. Server rendering always uses the inert snapshot;
// no server request can initialize or load document interaction state.
let browserResource: ReturnType<typeof createResource> | undefined;
function getBrowserResource() {
  if (typeof window === "undefined") return undefined;
  return (browserResource ??= createResource());
}

export const getBrowseMenuServerSnapshot = () => IDLE;
export const getBrowseMenuSnapshot = () => getBrowserResource()?.getSnapshot() ?? IDLE;
export function subscribeBrowseMenu(listener: () => void): () => void {
  return getBrowserResource()?.subscribe(listener) ?? (() => {});
}
export function ensureBrowseMenuLoaded(): Promise<BrowseMenuIndex> {
  const resource = getBrowserResource();
  if (!resource) return Promise.reject(new Error("Browse menu resource requires a browser"));
  return resource.ensureLoaded();
}
